// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function SeekAiChatTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="Seek Ai Chat"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of “Seek
        Ai Chat” (the “App”). By downloading, installing, accessing, or using
        the App, you agree to be bound by these Terms. If you do not agree, you
        must uninstall the App and discontinue all use. Where required by
        platform rules or local law, additional terms may apply; in case of
        conflict with App Store or Google Play terms, the platform terms may
        prevail to the extent required.
      </p>

      <div className={s.title}>
        2) Description of the App (AI Assistant, Non-Professional Use)
      </div>
      <p>
        The App provides an AI-powered chat interface that allows you to send
        messages and receive automated responses generated by machine learning
        models. You may use the App to ask questions, brainstorm ideas, draft
        text, or explore topics in a conversational format. The App is intended
        for general informational and productivity purposes only. It does not
        provide professional advice (including but not limited to legal,
        medical, financial, educational, or mental health advice) and should not
        be relied upon as a sole source for important decisions.
      </p>
      <p>
        AI-generated responses may be inaccurate, incomplete, out of date, or
        inappropriate for your specific situation. You are responsible for
        reviewing and verifying all information before acting on it or sharing
        it with others.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent.
      </p>

      <div className={s.title}>4) Accounts and Authentication</div>
      <p>
        The App may require or offer sign-in using Firebase Authentication or
        similar services (for example, sign-in with Google, Apple ID, or email).
        You are responsible for maintaining the confidentiality of your login
        credentials and for all activities that occur under your account. You
        must promptly notify us of any suspected unauthorized access or security
        issues. We may suspend or restrict access if we reasonably believe that
        your account has been compromised or used in violation of these Terms.
      </p>

      <div className={s.title}>5) AI Chat Behavior and Limitations</div>
      <p>
        Responses in the App are generated automatically by AI models based on
        patterns learned from data. The AI does not “know” you personally, has
        no independent awareness or judgment, and cannot guarantee factual
        accuracy. The AI may sometimes produce content that is incorrect,
        misleading, biased, or offensive, even when such output is not intended.
        You should treat all responses as suggestions or drafts, not as
        verified facts or instructions.
      </p>
      <p>
        You must not use AI responses as a substitute for professional advice,
        emergency services, or critical decision-making. For medical, mental
        health, legal, financial, or safety-critical questions, consult qualified
        professionals or official resources instead of relying on the App.
      </p>

      <div className={s.title}>6) Message Limits, Fair Use, and Free Tier</div>
      <p>
        The App may impose usage limits, such as a maximum number of messages
        per day or restrictions on the length of conversations, especially for
        free users. For example, the free version may allow only a limited
        number of messages per day and access only to the most recent chat,
        while paid subscribers may have higher or unlimited quotas and access to
        full history. Limits may be enforced using local counters, cloud data,
        or both.
      </p>
      <p>
        We may apply fair-use policies to prevent abuse or excessive load, such
        as rate limiting, soft caps, or temporary restrictions. These policies
        may change over time as needed to maintain service quality and
        stability.
      </p>

      <div className={s.title}>7) Chat History and Storage</div>
      <p>
        The App may store your messages and AI responses locally on your device
        (for example, in a local database such as SQLite or Hive) and, in some
        configurations, may also sync selected conversations to cloud services
        such as Firebase Firestore. In the free version, history access may be
        limited (for example, only the last conversation), while paid versions
        may unlock full history.
      </p>
      <p>
        Deleting the App or clearing local data may permanently remove stored
        conversations from your device. Cloud-stored data, where enabled, may
        persist until deleted according to in-app options or as described in the
        Privacy Policy. We do not guarantee long-term retention of chat history
        and may delete or aggregate old data for storage, performance, or legal
        reasons.
      </p>

      <div className={s.title}>8) Subscriptions, Trials, and Billing</div>
      <p>
        Certain features, such as unlimited messages, full chat history access,
        or additional tools, may be available only through paid auto-renewable
        subscriptions or other in-app purchases (“Subscriptions”) such as
        weekly or monthly plans. Typical pricing may include a free trial period
        followed by recurring charges, but exact pricing, durations, and offers
        may vary by region and may change over time as shown in the App and on
        the paywall screen.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are determined by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>9) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        features, higher message limits, or full history. An internet connection
        may be required to validate or refresh entitlements after long offline
        periods. If platform billing reverses, refunds, or flags a transaction
        as fraudulent, related entitlements may be limited, suspended, or
        revoked.
      </p>

      <div className={s.title}>10) Acceptable Use and Content Rules</div>
      <p>
        You agree to use the App only for lawful and respectful purposes and not
        to:
      </p>
      <ul>
        <li>
          send content that is illegal, hateful, harassing, discriminatory,
          sexually exploitative, or otherwise abusive;
        </li>
        <li>
          request or generate content that promotes self-harm, violence,
          terrorism, or other dangerous activities;
        </li>
        <li>
          attempt to use the App to create malware, phishing schemes, or other
          harmful code or instructions;
        </li>
        <li>
          share personal or sensitive information about others without consent,
          or attempt to deanonymize individuals;
        </li>
        <li>
          reverse engineer, decompile, or attempt to extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          circumvent technical protections, message limits, or paywalls
          separating free and paid features;
        </li>
        <li>
          use automated scripts, bots, or scraping tools to access or overload
          the App or its APIs.
        </li>
      </ul>
      <p>
        If we reasonably believe that your use violates these rules or
        applicable law, we may restrict, suspend, or terminate access, or apply
        additional safeguards.
      </p>

      <div className={s.title}>11) Third-Party Services and AI Providers</div>
      <p>
        The App sends your messages and related data to third-party AI
        providers, such as OpenAI, via secure HTTP requests, in order to obtain
        generated responses. These providers process your content under their
        own terms and privacy policies and may use it to provide, maintain, and
        improve their models in accordance with those policies and applicable
        agreements. The App also uses services such as Firebase (for
        authentication, storage, analytics, and messaging) and RevenueCat (for
        subscriptions).
      </p>
      <p>
        We do not control the internal operation of third-party models or
        infrastructure and are not responsible for outages, behavior, or policy
        changes by those providers. Some features may be added, changed, or
        removed based on third-party requirements or technical constraints.
      </p>

      <div className={s.title}>12) Intellectual Property</div>
      <p>
        You retain ownership of the original content you submit (for example,
        prompts, messages) and, subject to rights of third parties, are
        generally free to use AI-generated outputs for your own purposes, in
        accordance with applicable law and any restrictions imposed by the
        underlying model providers. You are responsible for ensuring that your
        use of outputs complies with copyright, trademark, privacy, and other
        laws.
      </p>
      <p>
        All software, user interface designs, trademarks, logos, and other
        materials provided with the App (excluding your content and model
        outputs) are owned by the publisher or its licensors and are protected
        by intellectual property laws. Subject to these Terms and any
        Subscription limitations, you are granted a limited, non-exclusive,
        non-transferable, revocable license to use the App and its features for
        personal use.
      </p>

      <div className={s.title}>13) Analytics and Push Notifications</div>
      <p>
        We may use analytics tools (for example, Firebase Analytics) to
        understand how the App is used, measure feature adoption, and improve
        reliability and user experience. Typical events may include app
        launches, screen views, paywall interactions, and purchase outcomes.
        Where required by law, we will obtain necessary permissions or provide
        options to manage analytics preferences.
      </p>
      <p>
        The App may also use push notifications (for example, via Firebase Cloud
        Messaging) to send reminders, product updates, or similar messages. You
        can control push notifications through system settings and, where
        available, in-app preferences.
      </p>

      <div className={s.title}>14) No Warranties</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement.
        We do not warrant that AI responses will be accurate, reliable, or
        suitable for any particular purpose, nor that the App will be available
        without interruption, errors, or security issues.
      </p>

      <div className={s.title}>15) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, loss of profits, loss of
        data (including loss of chat history or settings), or business
        interruption arising from or related to your use of or inability to use
        the App or AI responses. Our total aggregate liability to you for direct
        damages arising out of or relating to the App will be limited to the
        amount you paid for Subscriptions during the twelve (12) months prior to
        the claim, or the minimum amount permitted by applicable law if greater.
        Some jurisdictions do not allow certain limitations or exclusions, so
        parts of this section may not apply to you.
      </p>

      <div className={s.title}>16) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your content, your reliance on AI
        responses, your breach of these Terms, or your violation of any rights
        of a third party.
      </p>

      <div className={s.title}>17) Termination</div>
      <p>
        We may suspend or terminate your access to the App or to specific
        features at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, intellectual property, acceptable use,
        limitation of liability, and indemnification) will continue to apply
        after your access ends.
      </p>

      <div className={s.title}>18) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        Seek Ai Chat, which explains how information is collected, used,
        stored, and shared, including chat content, analytics, and technical
        data. In case of conflict between these Terms and the Privacy Policy
        regarding data processing, the Privacy Policy will guide how we handle
        personal data, while these Terms govern your use of the App and its
        features. You should review the Privacy Policy regularly to understand
        current practices.
      </p>

      <div className={s.title}>19) Changes to the App and to These Terms</div>
      <p>
        We may update the App, add or remove features (including free and
        premium capabilities), or modify these Terms from time to time. The
        “Last updated” date at the top of this document indicates when these
        Terms were last revised. Material changes may be highlighted in the App
        or via other reasonable means. By continuing to use the App after
        changes take effect, you accept the updated Terms. If you do not agree
        to the revised Terms, you must stop using the App and uninstall it.
      </p>

      <div className={s.title}>20) Contact Information</div>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account-specific
        or purchase-related matters.
      </p>
    </TermOfUseLayout>
  );
}
